import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import PCMBadge from "../components/PCMBadge";
import PasswordStrength from "../components/PasswordStrength";
import LanguageSelector from "../components/LanguageSelector";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User, Mail, Save, AlertCircle, Lock, Eye, EyeOff, Download, Trash2, Shield, Globe } from "lucide-react";

import { API } from "../utils/api";

const Profile = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(user?.name || "");
  
  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // RGPD state
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name }),
        credentials: "include",
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        toast.success(t('common.saved'));
      } else {
        toast.error(t('errors.updateError'));
      }
    } catch (error) {
      toast.error(t('errors.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation - check password strength
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword);
    
    if (newPassword.length < 8) {
      toast.error(t('password.errors.tooShort'));
      return;
    }
    
    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial) {
      toast.error(t('password.errors.noSpecial'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('password.errors.mismatch'));
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/users/me/change-password`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t('profile.passwordChanged'));
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.updateError'));
      }
    } catch (error) {
      toast.error(t('errors.updateError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  // RGPD: Export personal data
  const handleExportData = async () => {
    setExportLoading(true);
    
    try {
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/users/me/export-data`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `flow-mes-donnees-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(t('profile.dataExported'));
      } else {
        toast.error(t('errors.exportError'));
      }
    } catch (error) {
      toast.error(t('errors.exportError'));
    } finally {
      setExportLoading(false);
    }
  };

  // RGPD: Delete account
  const handleDeleteAccount = async () => {
    const deleteWord = i18n.language === 'en' ? 'DELETE' : 'SUPPRIMER';
    if (deleteConfirmation !== deleteWord) {
      toast.error(t('profile.deleteConfirmation'));
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/users/me/delete-account`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          password: deletePassword,
          confirmation: deleteConfirmation
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t('profile.accountDeleted'));
        logout();
        navigate("/");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const pcmDescriptions = {
    Analyseur: t('pcm.descriptions.Analyseur'),
    Empathique: t('pcm.descriptions.Empathique'),
    Persévérant: t('pcm.descriptions.Persévérant'),
    Énergiseur: t('pcm.descriptions.Énergiseur'),
    Promoteur: t('pcm.descriptions.Promoteur'),
    Imagineur: t('pcm.descriptions.Imagineur'),
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="profile-page">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">{t('profile.title')}</h1>
            <p className="text-slate-600">{t('profile.subtitle')}</p>
          </div>

          {/* Language Selector Card */}
          <Card className="mb-6 bg-white" data-testid="language-card">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <Globe className="w-5 h-5 mr-2 text-[#FF6B35]" />
                {t('profile.language')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">{t('profile.languageDesc')}</p>
              <LanguageSelector />
            </CardContent>
          </Card>

          {/* Profile Info */}
          <Card className="mb-6 bg-white" data-testid="profile-info-card">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <User className="w-5 h-5 mr-2 text-[#FF6B35]" />
                {t('profile.personalInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="flex items-center space-x-6 mb-6">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-[#101E4D] flex items-center justify-center">
                      <span className="text-white text-2xl font-bold">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-[#101E4D]">{user?.name}</h3>
                    <p className="text-slate-500">{user?.email}</p>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">{t('profile.fullName')}</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D]"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        value={user?.email || ""}
                        disabled
                        className="h-12 pl-10 bg-slate-100 border-slate-200 text-slate-500"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#101E4D] hover:bg-[#0A1330] text-white"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? t('common.saving') : t('common.save')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* PCM Profile */}
          <Card className="bg-white mb-6" data-testid="pcm-profile-card">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                {t('profile.pcmProfile')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!user?.pcm_base && !user?.pcm_phase ? (
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <AlertCircle className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-[#101E4D] mb-1">{t('profile.pcmNotConfigured')}</h4>
                    <p className="text-sm text-slate-600">
                      {t('profile.pcmNotConfiguredMessage')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Base */}
                  <div className="p-5 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-700">{t('profile.base')}</h4>
                      {user?.pcm_base && <PCMBadge type={user.pcm_base} />}
                    </div>
                    {user?.pcm_base && (
                      <p className="text-sm text-slate-600">
                        {pcmDescriptions[user.pcm_base]}
                      </p>
                    )}
                  </div>

                  {/* Phase */}
                  <div className="p-5 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-700">{t('profile.phase')}</h4>
                      {user?.pcm_phase && <PCMBadge type={user.pcm_phase} />}
                    </div>
                    {user?.pcm_phase && (
                      <p className="text-sm text-slate-600">
                        {pcmDescriptions[user.pcm_phase]}
                      </p>
                    )}
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-800">
                      <strong>Note :</strong> {t('profile.pcmNote')}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="bg-white" data-testid="change-password-card">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <Lock className="w-5 h-5 mr-2 text-[#FF6B35]" />
                {t('profile.changePassword')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-slate-700">{t('profile.currentPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] pr-10"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-slate-700">{t('profile.newPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] pr-10"
                        required
                        minLength={8}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <PasswordStrength password={newPassword} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700">{t('profile.confirmPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D]"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {passwordLoading ? t('common.saving') : t('profile.updatePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* RGPD - Data Protection */}
          <Card className="bg-white mt-6" data-testid="rgpd-card">
            <CardHeader>
              <CardTitle className="flex items-center text-[#101E4D]">
                <Shield className="w-5 h-5 mr-2 text-[#FF6B35]" />
                {t('profile.dataProtection')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Export Data */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <h4 className="font-medium text-[#101E4D] mb-2">{t('profile.exportData')}</h4>
                <p className="text-sm text-slate-600 mb-4">
                  {t('profile.exportDataDesc')}
                </p>
                <Button
                  onClick={handleExportData}
                  disabled={exportLoading}
                  variant="outline"
                  className="border-[#101E4D] text-[#101E4D] hover:bg-[#101E4D] hover:text-white"
                  data-testid="export-data-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportLoading ? t('profile.exporting') : t('profile.exportDataButton')}
                </Button>
              </div>

              {/* Delete Account */}
              <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                <h4 className="font-medium text-red-800 mb-2">{t('profile.deleteAccount')}</h4>
                <p className="text-sm text-red-700 mb-4">
                  {t('profile.deleteAccountDesc')}
                </p>
                
                {!showDeleteConfirm ? (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-100"
                    data-testid="show-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('profile.deleteAccountButton')}
                  </Button>
                ) : (
                  <div className="space-y-4 pt-2 border-t border-red-200 mt-4">
                    <div className="space-y-2">
                      <Label className="text-red-800">{t('profile.currentPassword')}</Label>
                      <Input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder={t('profile.enterPassword')}
                        className="bg-white border-red-200"
                        data-testid="delete-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-red-800">
                        {t('profile.deleteConfirmation').replace("'SUPPRIMER'", `'${i18n.language === 'en' ? 'DELETE' : 'SUPPRIMER'}'`).replace("'DELETE'", `'${i18n.language === 'en' ? 'DELETE' : 'SUPPRIMER'}'`)}
                      </Label>
                      <Input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder={i18n.language === 'en' ? 'DELETE' : 'SUPPRIMER'}
                        className="bg-white border-red-200"
                        data-testid="delete-confirmation-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={deleteLoading || !deletePassword || deleteConfirmation !== (i18n.language === 'en' ? 'DELETE' : 'SUPPRIMER')}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="confirm-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {deleteLoading ? t('profile.deleting') : t('profile.confirmDeletion')}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeletePassword("");
                          setDeleteConfirmation("");
                        }}
                        variant="outline"
                        className="border-slate-300"
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Legal Links */}
              <div className="flex flex-wrap gap-4 text-sm">
                <button
                  onClick={() => navigate("/privacy")}
                  className="text-[#FF6B35] hover:underline"
                >
                  {t('landing.footer.privacy')}
                </button>
                <button
                  onClick={() => navigate("/terms")}
                  className="text-[#FF6B35] hover:underline"
                >
                  {t('landing.footer.terms')}
                </button>
                <button
                  onClick={() => navigate("/legal")}
                  className="text-[#FF6B35] hover:underline"
                >
                  {t('landing.footer.legal')}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Profile;
